import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";
import { PhysicsBody } from "../../core/physics/PhysicsBody";
import { SpringForce } from "../../core/physics/forces/SpringForce";

interface AttractorBodies {
  bodyX: PhysicsBody;
  bodyY: PhysicsBody;
  springX: SpringForce;
  springY: SpringForce;
}

export class StringAttractor extends StringModule {
  private bodies = new WeakMap<StringObject, AttractorBodies>();

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "attractor";
    this._type = 2;

    this.cssProperties = [
      { name: "--attractor-x", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--attractor-y", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "radius", type: "number", fallback: this.settings["radius"] },
      { key: "strength", type: "number", fallback: this.settings["strength"] },
      {
        key: "attractor-follow-tension",
        type: "number",
        fallback: this.settings["attractor-follow-tension"] ?? 0.18,
      },
      {
        key: "attractor-follow-friction",
        type: "number",
        fallback: this.settings["attractor-follow-friction"] ?? 0.2,
      },
      {
        key: "attractor-follow-max-velocity",
        type: "number",
        fallback: this.settings["attractor-follow-max-velocity"] ?? 90,
      },
      {
        key: "attractor-return-tension",
        type: "number",
        fallback: this.settings["attractor-return-tension"] ?? 0.12,
      },
      {
        key: "attractor-return-friction",
        type: "number",
        fallback: this.settings["attractor-return-friction"] ?? 0.14,
      },
      {
        key: "attractor-return-max-velocity",
        type: "number",
        fallback: this.settings["attractor-return-max-velocity"] ?? 55,
      },
      {
        key: "attractor-max-offset",
        type: "number",
        fallback: this.settings["attractor-max-offset"] ?? 220,
      },
      {
        key: "attractor-sleep-epsilon",
        type: "number",
        fallback: this.settings["attractor-sleep-epsilon"] ?? 0.02,
      },
    ];
  }

  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>,
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("attractor-attached", false);
    object.setProperty("__next-attractor-x", 0);
    object.setProperty("__next-attractor-y", 0);
    object.setProperty("__prev-attractor-x", 0);
    object.setProperty("__prev-attractor-y", 0);
    object.setProperty("__needs-attractor-update", false);
    object.setProperty("event-attractor-name", this.getObjectEventName(object, "attractor:move"));
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    this.centers.attach(object, { nullifyTransform: true });

    const springX = new SpringForce(0, 0);
    const springY = new SpringForce(0, 0);
    const bodyX = new PhysicsBody();
    bodyX.addForce(springX);
    const bodyY = new PhysicsBody();
    bodyY.addForce(springY);
    this.bodies.set(object, { bodyX, bodyY, springX, springY });
  }

  override onObjectDisconnected(object: StringObject): void {
    this.centers.detach(object);
    this.bodies.delete(object);
  }

  private computeTarget(object: StringObject): { x: number; y: number; attached: boolean } {
    const { cx: centerX, cy: centerY } = this.centers.getCenter(object);
    const cursorX = this.data.cursor.smoothedX;
    const cursorY = this.data.cursor.smoothedY;
    const dx = cursorX - centerX;
    const dy = cursorY - centerY;
    const distance = Math.sqrt(dx ** 2 + dy ** 2);

    const radius = object.getProperty<number>("radius") ?? 0;
    const strength = object.getProperty<number>("strength") ?? 0;
    const attached = distance < radius;
    object.setProperty("attractor-attached", attached);

    return {
      x: attached ? dx * strength : 0,
      y: attached ? dy * strength : 0,
      attached,
    };
  }

  onFrame(_data: StringData): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      const bodies = this.bodies.get(object);
      if (!bodies) continue;

      const target = this.computeTarget(object);

      const tension = target.attached
        ? (object.getProperty<number>("attractor-follow-tension") ?? 0.18)
        : (object.getProperty<number>("attractor-return-tension") ?? 0.12);
      const friction = target.attached
        ? (object.getProperty<number>("attractor-follow-friction") ?? 0.2)
        : (object.getProperty<number>("attractor-return-friction") ?? 0.14);
      const maxVelocity = target.attached
        ? (object.getProperty<number>("attractor-follow-max-velocity") ?? 90)
        : (object.getProperty<number>("attractor-return-max-velocity") ?? 55);
      const maxOffset = object.getProperty<number>("attractor-max-offset") ?? 220;
      const sleepEpsilon = object.getProperty<number>("attractor-sleep-epsilon") ?? 0.02;

      bodies.springX.tension = tension;
      bodies.springX.target = target.x;
      bodies.springY.tension = tension;
      bodies.springY.target = target.y;
      bodies.bodyX.friction = friction;
      bodies.bodyY.friction = friction;
      bodies.bodyX.maxVelocity = maxVelocity;
      bodies.bodyY.maxVelocity = maxVelocity;
      bodies.bodyX.maxValue = maxOffset;
      bodies.bodyY.maxValue = maxOffset;

      bodies.bodyX.step();
      bodies.bodyY.step();

      const isSleeping =
        Math.abs(target.x) < sleepEpsilon &&
        Math.abs(target.y) < sleepEpsilon &&
        Math.abs(bodies.bodyX.value) < sleepEpsilon &&
        Math.abs(bodies.bodyY.value) < sleepEpsilon &&
        Math.abs(bodies.bodyX.velocity) < sleepEpsilon &&
        Math.abs(bodies.bodyY.velocity) < sleepEpsilon;

      if (isSleeping) {
        bodies.bodyX.reset();
        bodies.bodyY.reset();
        object.setProperty("__needs-attractor-update", false);
        continue;
      }

      const cssX = Math.round(bodies.bodyX.value * 100) / 100;
      const cssY = Math.round(bodies.bodyY.value * 100) / 100;
      const prevX = object.getProperty<number>("__prev-attractor-x") ?? 0;
      const prevY = object.getProperty<number>("__prev-attractor-y") ?? 0;
      const changed = Math.abs(cssX - prevX) > 0.01 || Math.abs(cssY - prevY) > 0.01;

      object.setProperty("__needs-attractor-update", changed);

      if (changed) {
        object.setProperty("__next-attractor-x", cssX);
        object.setProperty("__next-attractor-y", cssY);
      }
    }
  }

  override onMutate(): void {
    for (let i = 0; i < this.objects.length; i++) {
      const object = this.objects[i];
      const needsUpdate = object.getProperty<boolean>("__needs-attractor-update") === true;
      if (!needsUpdate) continue;

      const x = object.getProperty<number>("__next-attractor-x") ?? 0;
      const y = object.getProperty<number>("__next-attractor-y") ?? 0;

      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVar(el, "--attractor-x", x);
        styleTxn.setVar(el, "--attractor-y", y);
      });

      object.setProperty("__prev-attractor-x", x);
      object.setProperty("__prev-attractor-y", y);

      const eventName = object.getProperty<string>("event-attractor-name");
      if (eventName) {
        this.events.emit(eventName, {
          x,
          y,
          attached: object.getProperty<boolean>("attractor-attached") ?? false,
        });
      }

      object.setProperty("__needs-attractor-update", false);
    }
  }
}
